import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import API from './api';

/* ---------- Toast UI (bez biblioteka) ---------- */
function ToastContainer({ toasts, onClose }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map(t => {
        const color =
          t.type === 'success' ? 'bg-green-600' :
          t.type === 'error'   ? 'bg-red-600'   :
          t.type === 'warn'    ? 'bg-yellow-600':
                                 'bg-blue-600';
        const emoji =
          t.type === 'success' ? '✅' :
          t.type === 'error'   ? '⛔' :
          t.type === 'warn'    ? '⚠️' :
                                 'ℹ️';
        return (
          <div
            key={t.id}
            className={`${color} text-white rounded-lg shadow-lg px-4 py-3 min-w-[280px] max-w-[420px] animate-[fadein_120ms_ease-out]`}
          >
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none">{emoji}</span>
              <div className="flex-1 text-sm whitespace-pre-wrap">{t.text}</div>
              <button
                className="opacity-80 hover:opacity-100"
                onClick={() => onClose(t.id)}
                aria-label="Close"
              >✖</button>
            </div>
          </div>
        );
      })}
      <style>{`@keyframes fadein{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

/* ---------- Time helpers (robustno HH:mm) ---------- */
function normalizeTime(val) {
  if (!val) return '';
  const s = String(val).trim();

  // "3:05 PM" -> "15:05"
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const part = ampm[3].toUpperCase();
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  }

  // "8:00" ili "08:00[:ss]" -> "HH:mm"
  const m2 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m2) {
    const h = Math.min(23, Math.max(0, parseInt(m2[1],10)));
    const mi = Math.min(59, Math.max(0, parseInt(m2[2],10)));
    return `${String(h).padStart(2,'0')}:${String(mi).padStart(2,'0')}`;
  }

  return '';
}
function toMinutes(t) {
  const n = normalizeTime(t);
  if (!n) return 0;
  const [h, m] = n.split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}
function normalizeMapByDate(map) {
  const out = {};
  Object.entries(map || {}).forEach(([date, list]) => {
    out[date] = (list || [])
      .map(s => {
        const from = normalizeTime(s.from_time ?? s.from);
        const to   = normalizeTime(s.to_time   ?? s.to);
        if (!from || !to) return null;
        return { from_time: from, to_time: to };
      })
      .filter(Boolean);
  });
  return out;
}

/* ---------- Component ---------- */
function TeachersAvailability() {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);
  const [availability, setAvailability] = useState({});
  const [exceptions, setExceptions] = useState({});
  const [dayOffs, setDayOffs] = useState([]);
  const [calendarHighlights, setCalendarHighlights] = useState({});
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

  // Toast state
  const [toasts, setToasts] = useState([]);
  const pushToast = (type, text, ttlMs = 3800) => {
    const id = Math.random().toString(36).slice(2);
    setToasts(prev => [...prev, { id, type, text }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), ttlMs);
  };
  const closeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  // Workday iz settings-a + fallback
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });
  const dayStartM = toMinutes(workday.start || '08:00');
  const dayEndM   = toMinutes(workday.end   || '18:00');

  useEffect(() => {
    API.get('/workday-settings')
      .then(res => {
        const start = normalizeTime(res.data?.start || '08:00');
        const end   = normalizeTime(res.data?.end   || '18:00');
        setWorkday({ start, end });
      })
      .catch(() => { /* fallback već setovan */ });
  }, []);

  useEffect(() => {
    API.get('/teachers').then(res => setTeachers(res.data || []));
  }, []);

  useEffect(() => {
    API.get('/weeks')
      .then(res => setWeekOptions(res.data))
      .catch(err => console.error('Failed to load weeks:', err));
  }, []);

  useEffect(() => {
    if (!selectedTeacherId) return;
    API.get(`/availability/highlight?teacher_id=${selectedTeacherId}`)
      .then(res => setCalendarHighlights(res.data || {}))
      .catch(()=>setCalendarHighlights({}));
  }, [selectedTeacherId]);

  useEffect(() => {
    if (!selectedTeacherId || !selectedWeekStart) return;

    API.get(`/availability/week/${selectedWeekStart}?teacher_id=${selectedTeacherId}`)
      .then(res => {
        setAvailability(normalizeMapByDate(res.data?.availability || {}));
        setExceptions(normalizeMapByDate(res.data?.exceptions || {}));
        setDayOffs(res.data?.day_offs || []);
      })
      .catch(()=> {
        setAvailability({});
        setExceptions({});
        setDayOffs([]);
      });
  }, [selectedTeacherId, selectedWeekStart]);

  useEffect(() => {
    if (!selectedWeekStart) return;
    API.get(`/week-dates/${selectedWeekStart}`).then(res => setWeekDates(res.data || []));
  }, [selectedWeekStart]);

  const getTileClassName = ({ date }) => {
    const d = date.toISOString().slice(0, 10);
    if (calendarHighlights[d] === 'dayoff' || calendarHighlights[d] === 'none') return 'bg-red-200';
    if (calendarHighlights[d] === 'exception') return 'bg-yellow-200';
    if (calendarHighlights[d] === 'default') return 'bg-green-200';
    return '';
  };

  // klamp helper (koristi se na onBlur)
  const clampToWorkday = (hhmm) => {
    const m = Math.min(dayEndM, Math.max(dayStartM, toMinutes(hhmm)));
    return minutesToHHMM(m);
  };

  const showMessage = (type, text) => {
    setStatusMessage({ type, text });
    const t = (type === 'error') ? 'error' : (type === 'warn' ? 'warn' : 'success');
    pushToast(t, text);
  };

  const handleAddSlot = (target, setter, date, isException = false) => {
    if (!selectedTeacherId) return showMessage('error', 'Select a teacher first.');
    if (isException && (!availability[date] || availability[date].length === 0)) {
      return showMessage('error', `Cannot add exception for ${date} — no availability`);
    }
    if (dayOffs.includes(date)) {
      return showMessage('error', `Cannot add slots for ${date} — marked as Day Off`);
    }
    const existingSlots = target[date] || [];
    if (existingSlots.length > 0) {
      return showMessage('warn', `Slots already exist for ${date}. You can only modify or delete them.`);
    }

    setter(prev => {
      const out = { ...prev };
      if (isException) {
        const base = (availability[date] || [])[0] || { from_time: workday.start, to_time: workday.end };
        const exFromM = Math.max(dayStartM, toMinutes(base.from_time));
        const exToM   = Math.min(dayEndM,   toMinutes(base.to_time));
        const to60    = Math.min(exToM, exFromM + 60);
        out[date] = [{ from_time: minutesToHHMM(exFromM), to_time: minutesToHHMM(to60) }];
      } else {
        out[date] = [{ from_time: workday.start, to_time: workday.end }];
      }
      return out;
    });
  };

  const handleDeleteSlot = (setter, date, index) => {
    setter(prev => {
      const out = { ...prev };
      const rows = (out[date] || []).slice();
      rows.splice(index, 1);
      if (rows.length) out[date] = rows; else delete out[date];
      return out;
    });
  };

  // onChange: čuvamo RAW vrednost iz <input type="time"> (bez normalize/klamp)
  const handleTimeChange = (setter, date, index, field, rawValue) => {
    setter(prev => {
      const out = { ...prev };
      const rows = (out[date] || []).slice();
      const row  = { ...(rows[index] || {}) };
      row[field] = rawValue ?? '';
      rows[index] = row;
      out[date] = rows;
      return out;
    });
  };

  // onBlur: normalize + clamp + ensure from < to
  const handleTimeBlur = (setter, date, index) => {
    setter(prev => {
      const out = { ...prev };
      const rows = (out[date] || []).slice();
      const row  = { ...(rows[index] || {}) };

      let from = row.from_time ? normalizeTime(row.from_time) : workday.start;
      let to   = row.to_time   ? normalizeTime(row.to_time)   : minutesToHHMM(Math.min(dayEndM, toMinutes(from) + 30));

      from = clampToWorkday(from);
      to   = clampToWorkday(to);

      const f = toMinutes(from);
      let t   = toMinutes(to);
      if (t <= f) {
        t = Math.min(dayEndM, f + 30);
        to = minutesToHHMM(t);
      }

      row.from_time = from;
      row.to_time   = to;

      rows[index] = row;
      out[date] = rows;
      return out;
    });
  };

  const handleAddDayOff = async (date) => {
    if (!selectedTeacherId) return showMessage('error', 'Select a teacher first.');
    try {
      await API.post('/availability/dayoff', { day_off_date: date, reason: 'Day off', teacher_id: selectedTeacherId });
      setDayOffs(prev => [...prev, date]);
      setAvailability(prev => { const c = { ...prev }; delete c[date]; return c; });
      setExceptions(prev => { const c = { ...prev }; delete c[date]; return c; });
      await API.post(`/availability/week/${selectedWeekStart}`, {
        availability,
        exceptions,
        teacher_id: selectedTeacherId
      });
      showMessage('success', `Day Off added for ${date}`);
      const highlight = await API.get(`/availability/highlight?teacher_id=${selectedTeacherId}`);
      setCalendarHighlights(highlight.data || {});
    } catch {
      showMessage('error', 'Error adding Day Off.');
    }
  };

  const handleDeleteDayOff = (date) => {
    if (!selectedTeacherId) return showMessage('error', 'Select a teacher first.');
    API.post('/availability/dayoff/delete', { day_off_date: date, teacher_id: selectedTeacherId })
      .then(() => {
        setDayOffs(prev => prev.filter(d => d !== date));
        showMessage('success', `Day Off removed for ${date}`);
        API.get(`/availability/highlight?teacher_id=${selectedTeacherId}`).then(res => setCalendarHighlights(res.data || {}));
      })
      .catch(() => showMessage('error', 'Error removing Day Off.'));
  };

  const handleSaveAll = () => {
    if (!selectedTeacherId || !selectedWeekStart) return showMessage('error', 'Select a teacher and week first.');

    // safety sanitize pre slanja
    const sanitize = (map) => {
      const out = {};
      Object.entries(map || {}).forEach(([d, list]) => {
        out[d] = (list || []).map(s => {
          let from = normalizeTime(s.from_time || workday.start);
          let to   = normalizeTime(s.to_time   || minutesToHHMM(toMinutes(from) + 30));
          from = clampToWorkday(from);
          to   = clampToWorkday(to);
          const f = toMinutes(from), t0 = toMinutes(to);
          if (t0 <= f) to = minutesToHHMM(Math.min(dayEndM, f + 30));
          return { from_time: from, to_time: to };
        });
      });
      return out;
    };

    const safeAvailability = sanitize(availability);
    const safeExceptions   = sanitize(exceptions);

    setAvailability(safeAvailability);
    setExceptions(safeExceptions);

    API.post(`/availability/week/${selectedWeekStart}`, {
      availability: safeAvailability,
      exceptions: safeExceptions,
      teacher_id: selectedTeacherId
    })
      .then(() => {
        showMessage('success', 'Availability and exceptions saved successfully.');
        API.get(`/availability/highlight?teacher_id=${selectedTeacherId}`).then(res => setCalendarHighlights(res.data || {}));
      })
      .catch(() => showMessage('error', 'Error saving weekly data.'));
  };

  // Copy helpers
  const copyToNextWeek = () => {
    if (!selectedTeacherId || !selectedWeekStart) return showMessage('error', 'Select teacher and week');
    API.post(`/availability/copy-week/${selectedTeacherId}`, {
      from_week_start: selectedWeekStart,
      weeks_forward: 1,
      mode: 'append'
    }).then(res => {
      const t = res?.data?.targets?.[0];
      const aIns = t?.availability?.inserted ?? 0;
      const eIns = t?.exceptions?.inserted ?? 0;
      const dIns = t?.days_off?.inserted ?? 0;
      showMessage('success', `Copied to next week → availability:+${aIns}, exceptions:+${eIns}, days_off:+${dIns}`);
      // osveži highlight (nije kritično za sledeću nedelju, ali korektno)
      API.get(`/availability/highlight?teacher_id=${selectedTeacherId}`).then(r => setCalendarHighlights(r.data || {}));
    }).catch(() => showMessage('error', 'Copy failed'));
  };

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyWeeksForward, setCopyWeeksForward] = useState(1);
  const [copyMode, setCopyMode] = useState('append');
  const openCopyModal = () => setCopyModalOpen(true);
  const closeCopyModal = () => setCopyModalOpen(false);

  const confirmCopy = () => {
    if (!selectedTeacherId || !selectedWeekStart) return showMessage('error', 'Select teacher and week');
    const wf = Number(copyWeeksForward) || 1;
    API.post(`/availability/copy-week/${selectedTeacherId}`, {
      from_week_start: selectedWeekStart,
      weeks_forward: wf,
      mode: copyMode
    }).then(res => {
      const targets = res?.data?.targets || [];
      const aTotal = targets.reduce((s, t) => s + (t?.availability?.inserted || 0), 0);
      const eTotal = targets.reduce((s, t) => s + (t?.exceptions?.inserted || 0), 0);
      const dTotal = targets.reduce((s, t) => s + (t?.days_off?.inserted || 0), 0);
      showMessage('success', `Copied ${targets.length} week(s) (${copyMode}) → availability:+${aTotal}, exceptions:+${eTotal}, days_off:+${dTotal}`);
      closeCopyModal();
      API.get(`/availability/highlight?teacher_id=${selectedTeacherId}`).then(r => setCalendarHighlights(r.data || {}));
    }).catch(() => showMessage('error', 'Copy failed'));
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <h2 className="text-lg font-bold mb-1">Manage Teacher Availability</h2>
      <div className="text-sm text-gray-600 mb-6">
        Workday: <b>{workday.start}–{workday.end}</b>
      </div>

      <label className="block mb-2">Select a teacher:</label>
      <select
        className="border px-2 py-1 mb-4"
        value={selectedTeacherId}
        onChange={(e) => setSelectedTeacherId(e.target.value)}
      >
        <option value="">-- Choose teacher --</option>
        {teachers.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      <label className="block mb-2">Select a week:</label>
      <select
        className="border px-2 py-1 mb-6"
        value={selectedWeekStart}
        onChange={(e) => setSelectedWeekStart(e.target.value)}
      >
        <option value="">-- Choose week --</option>
        {weekOptions.map((week, idx) => (
          <option key={idx} value={week.value}>{week.label}</option>
        ))}
      </select>

      {/* Copy controls */}
      {selectedTeacherId && selectedWeekStart && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={copyToNextWeek}
            className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50"
          >
            Copy to next week
          </button>
          <button
            onClick={openCopyModal}
            className="bg-blue-500 text-white px-3 py-1 rounded disabled:opacity-50"
          >
            Copy to N weeks…
          </button>
        </div>
      )}

      {weekDates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {weekDates.map(({ dayName, date }) => {
            const isDayOff = dayOffs.includes(date);
            const hasAvail = availability[date]?.length > 0;
            const hasEx    = exceptions[date]?.length > 0;

            const isWeekend = ['Saturday', 'Sunday'].includes(dayName);
            const bgColor = isDayOff
              ? 'bg-red-100'
              : hasEx
              ? 'bg-yellow-100'
              : hasAvail
              ? 'bg-green-100'
              : isWeekend
              ? ''
              : 'bg-red-100';

            return (
              <div key={date} className={`border p-3 rounded shadow ${bgColor}`}>
                <h4 className="font-bold mb-2">{dayName} – {date}</h4>

                {isDayOff ? (
                  <div className="text-sm text-red-600 mb-2">Day Off</div>
                ) : (
                  <>
                    {(availability[date] || []).map((slot, index) => (
                      <div key={`a-${index}`} className="flex gap-2 mb-1 items-center">
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          min={workday.start}
                          max={workday.end}
                          value={slot.from_time || ''}
                          onChange={(e) => handleTimeChange(setAvailability, date, index, 'from_time', e.target.value)}
                          onBlur={() => handleTimeBlur(setAvailability, date, index)}
                          className="border px-2 py-1"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          min={workday.start}
                          max={workday.end}
                          value={slot.to_time || ''}
                          onChange={(e) => handleTimeChange(setAvailability, date, index, 'to_time', e.target.value)}
                          onBlur={() => handleTimeBlur(setAvailability, date, index)}
                          className="border px-2 py-1"
                        />
                        <button
                          onClick={() => handleDeleteSlot(setAvailability, date, index)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >🗑</button>
                      </div>
                    ))}

                    {(exceptions[date] || []).map((slot, index) => (
                      <div key={`e-${index}`} className="flex gap-2 mb-1 items-center text-yellow-700">
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          min={workday.start}
                          max={workday.end}
                          value={slot.from_time || ''}
                          onChange={(e) => handleTimeChange(setExceptions, date, index, 'from_time', e.target.value)}
                          onBlur={() => handleTimeBlur(setExceptions, date, index)}
                          className="border px-2 py-1"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          min={workday.start}
                          max={workday.end}
                          value={slot.to_time || ''}
                          onChange={(e) => handleTimeChange(setExceptions, date, index, 'to_time', e.target.value)}
                          onBlur={() => handleTimeBlur(setExceptions, date, index)}
                          className="border px-2 py-1"
                        />
                        <button
                          onClick={() => handleDeleteSlot(setExceptions, date, index)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >🗑</button>
                      </div>
                    ))}
                  </>
                )}

                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => isDayOff ? handleDeleteDayOff(date) : handleAddDayOff(date)}
                    className={`px-2 py-1 rounded text-sm ${isDayOff ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}`}
                  >
                    {isDayOff ? 'Remove Day Off' : 'Mark as Day Off'}
                  </button>
                  {!isDayOff && (
                    <>
                      <button
                        onClick={() => handleAddSlot(availability, setAvailability, date, false)}
                        className="bg-green-500 text-white px-2 py-1 rounded text-sm"
                      >
                        + Availability
                      </button>
                      <button
                        onClick={() => handleAddSlot(exceptions, setExceptions, date, true)}
                        className="bg-yellow-500 text-white px-2 py-1 rounded text-sm"
                      >
                        + Exception
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedWeekStart && (
        <div className="mt-8">
          <div className="flex gap-2 mb-3">
            <button onClick={handleSaveAll} className="bg-green-600 text-white px-4 py-2 rounded">
              Save All
            </button>
          </div>
          {statusMessage.text && (
            <div className={`mt-2 text-sm font-semibold ${statusMessage.type === 'success' ? 'text-green-600' : statusMessage.type === 'warn' ? 'text-yellow-600' : 'text-red-600'}`}>
              {statusMessage.text}
            </div>
          )}
        </div>
      )}

      <div className="mt-10">
        <h3 className="text-xl font-bold mb-2">Calendar Preview</h3>
        <Calendar tileClassName={getTileClassName} />
        <div className="mt-2 text-sm flex gap-4">
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-200 inline-block"></span> Availability</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-200 inline-block"></span> Exceptions</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-200 inline-block"></span> Day Off / None</span>
        </div>
      </div>

      {/* Copy modal */}
      {copyModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-4 w-full max-w-md shadow-xl">
            <h4 className="text-lg font-bold mb-4">Copy this week</h4>
            <div className="mb-3">
              <label className="block text-sm mb-1">Number of weeks forward:</label>
              <input
                type="number"
                min={1}
                max={52}
                value={copyWeeksForward}
                onChange={e => setCopyWeeksForward(e.target.value)}
                className="border px-2 py-1 w-24"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">Mode:</label>
              <select
                className="border px-2 py-1"
                value={copyMode}
                onChange={e => setCopyMode(e.target.value)}
              >
                <option value="append">Append (keep existing, no duplicates)</option>
                <option value="overwrite">Overwrite (replace target weeks)</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCopyModalOpen(false)} className="px-3 py-1 rounded border">Cancel</button>
              <button onClick={confirmCopy} className="px-3 py-1 rounded bg-blue-600 text-white">Copy</button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <ToastContainer toasts={toasts} onClose={closeToast} />
    </div>
  );
}

export default TeachersAvailability;



